import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, RefreshCw, KeyRound } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { AuthLayout, Field } from "@/pages/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

export default function Signup() {
  const { user, loading: authLoading, sendSignupOtp, verifySignupOtp } = useAuth();
  const navigate = useNavigate();
  usePageTitle("Create Account");

  // Step 1: Credentials, Step 2: OTP Verification
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Timers
  const [cooldown, setCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(600); // 10 minutes

  // Auto-redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Expiry countdown
  useEffect(() => {
    if (step !== 2 || expiresIn <= 0) return;
    const timer = setInterval(() => {
      setExpiresIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, expiresIn]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Enter your name.");
    if (!email.trim()) return setError("Enter your email.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");

    setLoading(true);
    try {
      await sendSignupOtp(name.trim(), email.trim(), password);
      toast.success(`Verification code sent to ${email.trim()}`);
      setStep(2);
      setCooldown(60);
      setExpiresIn(600);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (cooldown > 0 || loading) return;
    setError("");
    setLoading(true);
    try {
      await sendSignupOtp(name.trim(), email.trim(), password);
      toast.success("A new verification code has been sent.");
      setCooldown(60);
      setExpiresIn(600);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    const cleanOtp = otp.trim();
    if (cleanOtp.length !== 6) return setError("Enter the 6-digit verification code.");

    setLoading(true);
    try {
      await verifySignupOtp(name.trim(), email.trim(), password, cleanOtp);
      toast.success("Account verified and created!");
      navigate("/onboarding");
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={step === 1 ? "Create your account" : "Verify your email"}
      subtitle={
        step === 1
          ? "Start building your Creator DNA in minutes."
          : `We sent a 6-digit verification code to ${email}.`
      }
      footer={
        step === 1 ? (
          <div className="space-y-2 text-center text-xs">
            <p className="text-[11px] text-muted-foreground">
              By continuing, you agree to our{" "}
              <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
            </p>
            <p className="text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-accent hover:underline" data-testid="link-login">
                Log in
              </Link>
            </p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setError("");
            }}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Change email address
          </button>
        )
      }
    >
      {step === 1 ? (
        <form onSubmit={handleSendOtp} className="space-y-4" data-testid="signup-form">
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              data-testid="signup-name"
              required
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="signup-email"
              autoComplete="email"
              required
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              data-testid="signup-password"
              autoComplete="new-password"
              required
            />
          </Field>
          {error && (
            <p className="rounded-xl bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="signup-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="signup-submit"
            className="w-full rounded-full bg-accent text-white hover:bg-accent-hover shadow-sm"
          >
            {loading ? "Sending verification code…" : "Continue with Email"}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="otp-form">
          <div className="rounded-2xl border border-border/70 bg-card/60 p-4 text-center">
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
              <Mail className="h-5 w-5" />
            </div>
            <p className="text-xs text-muted-foreground">
              Enter the 6-digit code sent to <strong className="text-foreground">{email}</strong>
            </p>
            <div className="mt-2 text-xs font-medium text-accent">
              Code expires in {formatTime(expiresIn)}
            </div>
          </div>

          <Field label="6-Digit Verification Code">
            <div className="relative">
              <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(val);
                }}
                placeholder="123456"
                data-testid="signup-otp"
                autoFocus
                autoComplete="one-time-code"
                className="pl-10 text-center font-mono text-xl tracking-[0.35em] font-bold"
              />
            </div>
          </Field>

          {error && (
            <p className="rounded-xl bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="otp-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || otp.length !== 6}
            data-testid="verify-submit"
            className="w-full rounded-full bg-accent text-white hover:bg-accent-hover shadow-sm"
          >
            {loading ? "Verifying…" : "Verify & Activate Account"}
          </Button>

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">Didn't receive the code?</span>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={cooldown > 0 || loading}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                cooldown > 0 ? "text-muted-foreground/60 cursor-not-allowed" : "text-accent hover:underline"
              }`}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
              {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
